import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Briefcase, Plus, Search } from 'lucide-react';
import { useJobsStore } from '../store/jobsStore';
import { useMattersStore } from '../store/mattersStore';
import { statusMeta } from '../lib/meta';
import { EmptyState } from '../components/ui';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'running', label: 'In progress' },
  { value: 'failed', label: 'Failed' },
  // Research not yet filed under a case (IA, July 2026) — surfaced so
  // unfiled work stays findable now that Documents lost top-level billing.
  { value: 'unfiled', label: 'Not filed' },
] as const;

type Filter = (typeof FILTERS)[number]['value'];

/**
 * Research library (§4.3) — every memo, running or done, findable again.
 * Client-side search + status filter over the jobsStore cache; each row
 * names its case (the app's connective tissue, §5).
 */
export function ResearchLibrary() {
  const { jobs, lastFetched, isFetching, fetchJobs } = useJobsStore();
  const { matters, fetchMatters } = useMattersStore();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  useEffect(() => {
    fetchJobs(100);
    fetchMatters();
  }, [fetchJobs, fetchMatters]);

  const caseTitleById = useMemo(() => {
    const map = new Map<string, string>();
    matters.forEach((m) => map.set(m.id, m.title));
    return map;
  }, [matters]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((job) => {
      if (filter === 'completed' && job.status !== 'completed') return false;
      if (filter === 'running' && job.status !== 'processing' && job.status !== 'pending') return false;
      if (filter === 'failed' && job.status !== 'failed') return false;
      if (filter === 'unfiled' && job.matter_id) return false;
      if (!q) return true;
      const caseName = job.matter_id ? caseTitleById.get(job.matter_id) ?? '' : '';
      return [job.title, job.description, caseName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [jobs, query, filter, caseTitleById]);

  const isFirstLoad = jobs.length === 0 && (isFetching || lastFetched === null);

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      {/* data-tour: stable anchor for the walkthrough — exists even with zero memos */}
      <div data-tour="research-library" className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Research</h1>
          <p className="text-legal-500 mt-1 text-[15px]">
            Every memo you've run — tap one to read it or keep working on it.
          </p>
        </div>
        <Link
          to="/app/research/new"
          data-track="research-library-new"
          className="flex items-center space-x-2 px-4 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors press shrink-0"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">New research</span>
          <span className="sm:hidden">New</span>
        </Link>
      </div>

      {/* Status filter + search */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`px-3.5 py-2 rounded-full text-sm font-medium transition-colors press ${
                filter === f.value
                  ? 'bg-accent text-legal-900'
                  : 'bg-white border border-legal-200 text-legal-600 hover:border-legal-300'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-legal-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search question, memo or case…"
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-legal-200 rounded-lg bg-white text-legal-800 placeholder-legal-400 focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
      </div>

      {isFirstLoad ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-16 bg-legal-100 rounded-card" />
          ))}
        </div>
      ) : shown.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title={query || filter !== 'all' ? 'No matches' : 'No research yet'}
          message={
            query || filter !== 'all'
              ? 'Nothing matches that search or filter.'
              : 'Ask your first legal question — a full cited memorandum takes a few minutes.'
          }
          action={
            !query &&
            filter === 'all' && (
              <Link
                to="/app/research/new"
                data-track="research-library-empty-new"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors press"
              >
                <Plus className="h-4 w-4" /> Ask a legal question
              </Link>
            )
          }
        />
      ) : (
        <div className="bg-white rounded-card border border-legal-200 divide-y divide-legal-100">
          {shown.map((job) => {
            const meta = statusMeta(job.status);
            const caseName = job.matter_id ? caseTitleById.get(job.matter_id) : undefined;
            const date = new Date(job.created_at).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            });
            return (
              <Link
                key={job.id}
                to={`/app/research/${job.id}`}
                className="group flex items-center gap-3 px-4 py-3.5 hover:bg-legal-50 transition-colors"
              >
                <span className={`h-2 w-2 rounded-full shrink-0 ${meta.dot}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-legal-900 truncate group-hover:text-accent-700 transition-colors">
                    {job.title}
                  </span>
                  <span className="block text-xs text-legal-400 mt-0.5 truncate">
                    {caseName ? (
                      <span className="inline-flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {caseName}
                        <span aria-hidden="true"> · </span>
                      </span>
                    ) : null}
                    {date}
                  </span>
                </span>
                <span
                  className={`hidden sm:inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${meta.bg} ${meta.text}`}
                >
                  {meta.label}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
